import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface CurrencyInfo {
  code: string
  symbol: string
  name: string
  decimals: number
  symbolAfter?: boolean // e.g. some locales put symbol after the number
}

export const SUPPORTED_CURRENCIES: CurrencyInfo[] = [
  { code: "USD", symbol: "$",  name: "US Dollar",          decimals: 2 },
  { code: "EUR", symbol: "€",  name: "Euro",               decimals: 2 },
  { code: "GBP", symbol: "£",  name: "British Pound",      decimals: 2 },
  { code: "JPY", symbol: "¥",  name: "Japanese Yen",       decimals: 0 },
  { code: "KRW", symbol: "₩",  name: "Korean Won",         decimals: 0 },
  { code: "CAD", symbol: "CA$",name: "Canadian Dollar",    decimals: 2 },
  { code: "AUD", symbol: "A$", name: "Australian Dollar",  decimals: 2 },
  { code: "CNY", symbol: "¥",  name: "Chinese Yuan",       decimals: 2 },
  { code: "INR", symbol: "₹",  name: "Indian Rupee",       decimals: 2 },
  { code: "BRL", symbol: "R$", name: "Brazilian Real",     decimals: 2 },
  { code: "MXN", symbol: "MX$",name: "Mexican Peso",       decimals: 2 },
  { code: "PHP", symbol: "₱",  name: "Philippine Peso",    decimals: 2 },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah",  decimals: 0 },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar",   decimals: 2 },
  { code: "THB", symbol: "฿",  name: "Thai Baht",          decimals: 2 },
  { code: "SEK", symbol: "kr", name: "Swedish Krona",      decimals: 2, symbolAfter: true },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone",    decimals: 2, symbolAfter: true },
  { code: "DKK", symbol: "kr", name: "Danish Krone",       decimals: 2, symbolAfter: true },
  { code: "PLN", symbol: "zł", name: "Polish Złoty",       decimals: 2, symbolAfter: true },
  { code: "CZK", symbol: "Kč", name: "Czech Koruna",       decimals: 2, symbolAfter: true },
  { code: "HUF", symbol: "Ft", name: "Hungarian Forint",   decimals: 0, symbolAfter: true },
  { code: "NZD", symbol: "NZ$",name: "New Zealand Dollar", decimals: 2 },
  { code: "CHF", symbol: "CHF",name: "Swiss Franc",        decimals: 2 },
  { code: "ZAR", symbol: "R",  name: "South African Rand", decimals: 2 },
  { code: "TRY", symbol: "₺",  name: "Turkish Lira",       decimals: 2 },
  { code: "AED", symbol: "د.إ",name: "UAE Dirham",         decimals: 2 },
  { code: "SAR", symbol: "﷼",  name: "Saudi Riyal",        decimals: 2 },
  { code: "TWD", symbol: "NT$",name: "Taiwan Dollar",      decimals: 0 },
  { code: "VND", symbol: "₫",  name: "Vietnamese Dong",    decimals: 0, symbolAfter: true },
]

interface CurrencyState {
  currency: string // ISO code, default "USD"
  rates: Record<string, number> // rates relative to USD
  ratesLoadedAt: number
  setCurrency: (code: string) => void
  setRates: (rates: Record<string, number>) => void
}

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set) => ({
      currency: "USD",
      rates: { USD: 1 },
      ratesLoadedAt: 0,
      setCurrency: (code) => set({ currency: code }),
      setRates: (rates) => set({ rates, ratesLoadedAt: Date.now() }),
    }),
    { name: "goonru-currency" }
  )
)

/** Format USD cents into the selected display currency */
export function formatCurrency(usdCents: number, currency: string, rates: Record<string, number>): string {
  const info = SUPPORTED_CURRENCIES.find((c) => c.code === currency) ?? SUPPORTED_CURRENCIES[0]
  const rate = rates[currency] ?? 1
  const converted = (usdCents / 100) * rate
  const fixed = converted.toFixed(info.decimals)
  // Thousands separator
  const parts = fixed.split(".")
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  const number = parts.join(".")
  return info.symbolAfter ? `${number} ${info.symbol}` : `${info.symbol}${number}`
}
